/**
 * Satellite club scenario packs.
 *
 * One ClubCierge backbone, three branded satellite pathways. Each pack defines
 * a single named member, one governed action, one authority gate, and the
 * shared/withheld evidence boundary that the backend signs through KATLAS.
 *
 * The shape mirrors what the backend's `/api/satellites/{kind}` endpoint
 * accepts and what `/api/satellites/{kind}/approve` returns, so receipts can
 * be re-rendered identically to the main ClubCierge receipts.
 */
export const SATELLITES = {
  classic: {
    id: "classic",
    workingName: "ClassicCierge",
    brand: "Classic car club",
    accent: "#A24F2A",
    heroTagline: "Heritage, motoring, events, provenance, local branches, registers, spares, insurance.",
    proofLine: "Vehicle, event and benefit eligibility with signed evidence receipts.",
    member: {
      name: "Eleanor Hartwell",
      role: "Member · Cotswold Branch",
      asset: "1968 MGB Roadster · BWP 472 G · concours grade 2",
    },
    action: {
      label: "Approve event eligibility · 2026 Cotswold Spring Run",
      summary: "Eleanor's MGB Roadster passes branch eligibility for the Cotswold Spring Run. Officer confirms membership, vehicle registration and insurance evidence; concours grade and historic-vehicle status are referenced, not duplicated.",
    },
    authority: {
      label: "Branch Register Lead",
      person: "Harold Pemberton",
    },
    shared_fields: [
      "Membership status",
      "Vehicle registration reference",
      "Historic-vehicle classification",
      "Insurance evidence reference",
    ],
    withheld_fields: [
      "Full address",
      "Driving licence number",
      "Vehicle valuation document",
      "Private contact details",
    ],
    car: {
      custody: "Member / vehicle profile controls vehicle and membership evidence.",
      authority: "Branch officer / register lead approves event or benefit eligibility under policy gate CAR-CLUBCIERGE-001.",
      receipt: "KATLAS signs the approval record and the evidence boundary.",
    },
    commercial: "Insurance, spares, valuations, events, restoration evidence.",
  },

  rider: {
    id: "rider",
    workingName: "RiderCierge",
    brand: "Rider club",
    accent: "#5E2B1D",
    heroTagline: "Membership, rallies, ride-outs, guest permissions, safety, travel, insurance.",
    proofLine: "Ride-outs, rallies and member permissions with accountable approvals.",
    member: {
      name: "Dexter Cole",
      role: "Member · Chapter 47",
      asset: "Rider profile · pillion permission requested · emergency contact on file",
    },
    action: {
      label: "Approve participation · Highland Coast Rally 2026",
      summary: "Dexter's chapter membership, ride history and emergency contact pass eligibility for the Highland Coast Rally. Road captain confirms guest/pillion rule and safety briefing acknowledgement; full medical or contact data is not shared with organisers.",
    },
    authority: {
      label: "Road Captain",
      person: "Maya Ortega",
    },
    shared_fields: [
      "Chapter membership status",
      "Ride-out tier",
      "Guest / pillion permission",
      "Safety briefing acknowledgement",
    ],
    withheld_fields: [
      "Emergency contact phone number",
      "Medical declaration details",
      "Insurance policy number",
      "Home address",
    ],
    car: {
      custody: "Rider / member profile controls membership and eligibility evidence.",
      authority: "Road captain or club officer approves participation under policy gate CAR-CLUBCIERGE-001.",
      receipt: "KATLAS signs the ride / rally approval with shared and withheld evidence.",
    },
    commercial: "Rallies, insurers, travel partners, safety governance, member benefits.",
  },

  training: {
    id: "training",
    workingName: "TrainingCierge",
    brand: "Training / awareness centre",
    accent: "#1F4A4A",
    heroTagline: "Careful, professional, role-gated access, supervision, placements, consent, safeguarding-conscious administration.",
    proofLine: "Role-gated access, supervision and evidence boundaries for professional settings.",
    member: {
      name: "Priya Acharya",
      role: "Learner practitioner · Cohort C",
      asset: "Qualification record · supervision log · placement plan",
    },
    action: {
      label: "Approve supervised placement · Q2 community attachment",
      summary: "Priya's qualification status, supervision log and consent boundary are reviewed for the Q2 community placement. Supervisor confirms purpose-bound access; sensitive personal background, full medical history and case notes are not shared with the placement host.",
    },
    authority: {
      label: "Supervisor / Training Officer",
      person: "Dr Aaron Beck (Training Lead)",
    },
    shared_fields: [
      "Qualification status reference",
      "Supervision log reference",
      "Placement consent acknowledgement",
      "Cohort and module reference",
    ],
    withheld_fields: [
      "Sensitive personal background",
      "Full medical history",
      "Case notes",
      "Personal contact details",
    ],
    car: {
      custody: "Practitioner / learner profile controls qualification, placement and supervision evidence.",
      authority: "Supervisor or training officer approves the bounded action under policy gate CAR-CLUBCIERGE-001.",
      receipt: "KATLAS signs the approval and the evidence boundary.",
    },
    commercial: "Training administration, supervision evidence, compliance, consent boundaries, safe AI administration.",
    boundary: "This is operational governance around access, supervision, placements, evidence and consent boundaries. It is not AI therapy, clinical decisioning or clinical triage.",
  },
};

export const SATELLITE_LIST = [SATELLITES.classic, SATELLITES.rider, SATELLITES.training];
